import { append, propEq, reject, find } from 'ramda'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Box,
  Dialog,
  Stack,
  Button,
  Alert,
  TextLink,
  Text,
  Checkbox as DSCheckbox,
  Divider,
  Columns,
  Column,
  Bleed,
  Avatar
} from '@machobear/design-system'
import { Userpilot } from 'userpilot'
import VerifiedBadge from '@/components/verified-badge'
import useCompany from '@/hooks/use-company'
import { formatFullName } from '@/utils/format-name'
import { getImageUrl } from '@/utils/image'
import isVerified from '@/utils/is-verified'
import toBoolean from '@/utils/to-boolean'

import useProfile from '@/hooks/use-profile'
import ENV_VARS from '@/config'

const { WEB_URL } = ENV_VARS

const isChecked = (userId, selectedUsers) => toBoolean(find(propEq('id', userId))(selectedUsers))

const isDisabled = (userId, disabledUsers) => toBoolean(find(propEq('id', userId))(disabledUsers))

const User = ({ user, toggleSelected, selectedUsers, disabledUsers }) => {
  const disabled = isDisabled(user?.id, disabledUsers)
  const company = useCompany(user?.company_id)
  const name = formatFullName(user)

  return (
    <Box
      onClick={() => toggleSelected(user)}
      cursor={disabled ? 'default' : 'pointer'}
      role="option"
      aria-label={name}
    >
      <Columns>
        <Column>
          <Box display="flex" alignItems="center" height="full">
            <Stack space="small">
              <Columns alignY="center">
                <Column width="content" align="left">
                  <DSCheckbox
                    id="landlord-modal-checkbox"
                    checked={isChecked(user?.id, selectedUsers)}
                    onChange={() => toggleSelected(user)}
                    disabled={disabled}
                    type="checkbox"
                  />
                </Column>
                <Column alignY="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ wordBreak: 'break-all', minWidth: '225px' }}
                  >
                    <Text size="standard" disabled={disabled}>
                      {name}
                    </Text>
                    <VerifiedBadge
                      type="id"
                      width={30}
                      height={22}
                      bottom={-1}
                      verified={isVerified(user, company)}
                    />
                  </Box>
                </Column>
              </Columns>
            </Stack>
          </Box>
        </Column>
        <Column>
          <Box
            display="flex"
            justifyContent="flexEnd"
            alignItems="center"
            style={{
              opacity: disabled ? 0.6 : 1
            }}
          >
            <Avatar
              img={getImageUrl(user?.avatar?.aws_s3_key, {
                width: 96,
                height: 96
              })}
              shape="circle"
              size="small"
              name={name}
            />
          </Box>
        </Column>
      </Columns>
    </Box>
  )
}

const AlertContent = ({ trackingId }) => {
  const { t } = useTranslation()
  const data = useProfile()
  const currentPlan = data.company_info?.base_plan_name
  const handleClickPricing = () => {
    Userpilot.track(trackingId)
  }
  if (currentPlan?.includes('essentials')) {
    return (
      <Alert tone="promote">
        <Trans t={t} i18nKey="l.listing.edit.landlord_managment.add_modal.essentials_alert">
          <Text size="small">
            Need more landlords? Add up to 3 additional landlords with our{' '}
            <TextLink href={`${WEB_URL}/pricing`} target="_blank" onClick={handleClickPricing}>
              Growth plan
            </TextLink>
          </Text>
        </Trans>
      </Alert>
    )
  } else if (currentPlan?.includes('growth')) {
    return (
      <Alert tone="promote">
        <Trans t={t} i18nKey="l.listing.edit.landlord_managment.add_modal.growth_alert">
          <Text size="small">
            Need more landlords? Add up to 10 additional landlords with our{' '}
            <TextLink href={`${WEB_URL}/pricing`} target="_blank" onClick={handleClickPricing}>
              Business plan
            </TextLink>
          </Text>
        </Trans>
      </Alert>
    )
  } else if (currentPlan?.includes('business')) {
    return (
      <Alert tone="promote">
        <Trans t={t} i18nKey="l.listing.edit.landlord_managment.add_modal.business_alert">
          <Text size="small">
            Need more landlords?{' '}
            <TextLink href="mailto:support@liv.rent" target="_blank">
              Contact us
            </TextLink>
          </Text>
        </Trans>
      </Alert>
    )
  }
}

const UserPicker = ({
  title = '',
  subtitle = '',
  maxSelect,
  allUsers = [],
  activeUsers = [],
  disabledUsers = [],
  setActiveUsers,
  buttonLabel = '',
  visible,
  onClose = () => {},
  showLandlordAlerts = false,
  showPrimaryLandlordAlert = false,
  alertTrackingId = ''
}) => {
  const { t } = useTranslation()
  const [selectedUsers, setSelectedUsers] = useState(activeUsers)

  useEffect(() => {
    setSelectedUsers(activeUsers)
  }, [visible])

  const toggleSelected = user => {
    if (isDisabled(user?.id, disabledUsers)) return
    if (isChecked(user?.id, selectedUsers)) {
      setSelectedUsers(reject(propEq('id', user?.id), selectedUsers))
      return
    }
    if (maxSelect === undefined || selectedUsers?.length < maxSelect) {
      setSelectedUsers(append(user, selectedUsers))
    }
  }

  return (
    <Dialog
      id="show-secondary-users-modal"
      title={title ? title : 'Add additional landlords'}
      width="small"
      open={visible}
      onClose={onClose}
    >
      <Box paddingBottom="gutter">
        <Stack space="gutter">
          {showPrimaryLandlordAlert && (
            <Alert tone="caution">
              <Trans t={t} i18nKey="l.listing.edit.landlord_managment.remove_primary.alert">
                <Text size="small">Removing yourself will revoke your access to this listing.</Text>
              </Trans>
            </Alert>
          )}
          <Text weight="medium">
            {subtitle
              ? subtitle
              : t('l.listing.edit.landlord_managment.add_modal.select_from', 'Select from:')}
          </Text>
          <Box width="full" height="full" role="listbox">
            <Stack space="gutter">
              {allUsers?.map(user => (
                <User
                  key={`user-${user?.id}`}
                  user={user}
                  toggleSelected={toggleSelected}
                  selectedUsers={selectedUsers}
                  disabledUsers={disabledUsers}
                />
              ))}
            </Stack>
          </Box>
          {showLandlordAlerts && (
            <AlertContent activeUsers={activeUsers} trackingId={alertTrackingId} />
          )}
        </Stack>
      </Box>
      <Divider weight="regular" />
      <Box position="sticky" bottom={0}>
        <Bleed horizontal="large" bottom="large">
          <Box
            display="flex"
            justifyContent="flexEnd"
            background="body"
            borderTop="borderNeutralLight"
            padding="large"
          >
            <Box style={{ width: buttonLabel ? 120 : 74 }}>
              <Button
                variant="solid"
                onClick={() => {
                  setActiveUsers(selectedUsers)
                  onClose()
                }}
                disabled={selectedUsers?.length < 1}
              >
                {buttonLabel || t('common.add', 'Add')}
              </Button>
            </Box>
          </Box>
        </Bleed>
      </Box>
    </Dialog>
  )
}

export default UserPicker
